import { useState, useCallback, MouseEvent } from 'react';
import toast from 'react-hot-toast';
import { useTheme } from 'styled-components';
import { Group } from '../../../API/types/Groups';
import Modal from '../../../components/common/Modal/Modal';
import Tags from '../../../components/common/Tags';
import { Button } from '../../../components/ui/Button/Button';
import { ListItemWrapper } from '../../../components/ui/ListItemWrapper/ListItemWrapper';
import { getFormattedDate } from '../../../utils/dateUtils';
import { IconKeys } from '../../../components/common/Icon/IconKeys';
import useGetRole from '../../../API/hooks/useGetRole';
import useAddAllVehiclesToGroup from '../../../API/hooks/groups/useAddAllVehiclesToGroup';
import { ThemeType } from '../../../theme/themeType';
import DeleteModal from '../../../components/common/DeleteModal/DeleteModal';
import ConfirmModal from '../../../components/common/ConfirmModal/ConfirmModal';
import CheckBox from '../../../components/common/CheckBox/CheckBox';
import useDeleteGroup from '../../../API/hooks/groups/useDeleteGroup';
import EditGroup from '../Modals/EditGroup/EditGroup';
import DetailsGroup from '../Modals/DetailsGroup/DetailsGroup';

type Props = {
  item?: Group;
  isSelecting?: boolean;
  isSelected?: boolean;
  onSelect?: (checked: boolean, id: string) => void;
};
function GroupsItem({
  item, isSelected, isSelecting, onSelect,
}: Props): JSX.Element {
  const theme = useTheme() as ThemeType;
  const {
    isFleetViewer, isFleetManager, isFleetAdmin, isSupport, isAdmin,
  } = useGetRole();
  const canManage = !isFleetViewer && (isFleetManager || isFleetAdmin || isSupport || isAdmin);
  const [modalEditState, toggleModalEditState] = useState(false);
  const [modalDeleteState, toggleModalDeleteState] = useState(false);
  const [addAllVehiclesModalOpen, toggleAddAllVehiclesModal] = useState(false);
  const [modalDetailsState, toggleModalDetailsState] = useState(false);
  const onSubmitSuccess = (): void => {};
  const onSubmitFail = (errorMessage: string): void => { toast.error(errorMessage); };
  const { mutate: deleteGroup } = useDeleteGroup(onSubmitSuccess, onSubmitFail);
  const { mutate: addAllVehiclesToGroup } = useAddAllVehiclesToGroup(() => toggleAddAllVehiclesModal(false), onSubmitFail);

  const {
    _id: id,
    createdAt,
    name,
  } = item!;

  const handleClick = useCallback((): void => {
    if (isSelecting) {
      if (onSelect) {
        onSelect!(isSelected as boolean, id);
      }
    } else {
      toggleModalDetailsState(true);
    }
  }, [id, isSelecting, isSelected, onSelect]);

  const handleEditGroup = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    toggleModalEditState(true);
  };

  const handleDeleteGroup = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    toggleModalDeleteState(true);
  };

  const handleAddAllVehiclesToGroup = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    toggleAddAllVehiclesModal(true);
  };

  const addAllVehiclesAction = (): void => {
    addAllVehiclesToGroup({
      fleetId: item?.fleetId as string,
      groupId: item?._id as string,
    });
  };

  const deleteGroupAction = (): void => {
    if (canManage && item) {
      deleteGroup({
        fleetId: item?.fleetId,
        id,
      });
    }
  };

  return (
    <>
      <ListItemWrapper onClick={handleClick}>
        {isSelecting && (
          <CheckBox
            label=""
            state={isSelected}
            onChange={() => {}}
            name={`driver-${id}`}
          />
        )}
        <Tags.ItemOverview
          avatarAlt="group"
          title={name}
          subtitle=""
        />

        <Tags.LabeledIcon
          label={`Added on ${getFormattedDate(createdAt)}`}
          icon={IconKeys.DATE}
          flexible
        />
        {/* <Tags.LabeledIcon
          label={`Last active ${getFormattedDate(updatedAt)}`}
          icon={IconKeys.PULSE}
          flexible
        /> */}
        <div className="tags-container">
          <Button
            height="26px"
            width="120px"
            padding="8px 8px"
            background={theme.colors.outline}
            hoverBackground={theme.colors.inactive}
            onClick={() => toggleModalDetailsState(true)}
          >
            Details
          </Button>
          <Button
            height="26px"
            width="120px"
            padding="8px 8px"
            background={theme.colors.outline}
            hoverBackground={theme.colors.inactive}
            onClick={(e) => handleEditGroup(e)}
          >
            Edit
          </Button>
          <Button
            height="26px"
            width="150px"
            padding="8px 8px"
            background={theme.colors.outline}
            hoverBackground={theme.colors.inactive}
            onClick={(e) => handleAddAllVehiclesToGroup(e)}
          >
            Add all vehicles
          </Button>
          <Button
            height="26px"
            width="120px"
            padding="8px 8px"
            background={theme.colors.outline}
            hoverBackground={theme.colors.red}
            onClick={(e) => handleDeleteGroup(e)}
          >
            Remove
          </Button>
        </div>
      </ListItemWrapper>
      {modalEditState && (
        <Modal
          title="Edit group"
          state={modalEditState}
          toggleState={toggleModalEditState}
        >
          <EditGroup data={item} toggleModal={toggleModalEditState} />
        </Modal>
      )}
      {addAllVehiclesModalOpen && (
        <Modal
          title="Add all vehicles to group"
          state={addAllVehiclesModalOpen}
          toggleState={toggleAddAllVehiclesModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <ConfirmModal
            title="Do you want to add all vehicles to this group?"
            onClose={() => toggleAddAllVehiclesModal(false)}
            onConfirm={() => addAllVehiclesAction()}
          />
        </Modal>
      )}
      {modalDeleteState && (
        <Modal
          title="Delete group"
          state={modalDeleteState}
          toggleState={toggleModalDeleteState}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title="Are you sure you want to delete this group?"
            onClose={() => toggleModalDeleteState(false)}
            onDelete={deleteGroupAction}
          />
        </Modal>
      )}
      {modalDetailsState && (
        <Modal
          title="Group details*"
          state={modalDetailsState}
          toggleState={toggleModalDetailsState}
          settings={{
            minWidth: '800px',
          }}
        >
          <DetailsGroup groupId={id} fleetId={item?.fleetId} toggleModal={toggleModalDetailsState} />
        </Modal>
      )}
    </>
  );
}

export default GroupsItem;
